import { z } from 'zod';
import { UserRoleSchema, ReportLanguageSchema, FontSizeSchema, ThemeSchema } from '../enums';

// User Preferences Schema
export const UserPreferencesSchema = z.object({
  reportLanguage: ReportLanguageSchema.default('en'),
  fontSize: FontSizeSchema.default('md'),
  theme: ThemeSchema.default('system'),
  slipPrinter: z.string().optional(),
  reportPrinter: z.string().optional()
});

export type UserPreferences = z.infer<typeof UserPreferencesSchema>;

// Base User Schema (without sensitive data)
export const BaseUserSchema = z.object({
  userId: z.string().optional(), // MongoDB ObjectId
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email format'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  role: UserRoleSchema,
  preferences: UserPreferencesSchema.default({}),
  lastLoginTime: z.date().optional(),
  lastLoginDevice: z.string().optional(),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional()
});

export type BaseUser = z.infer<typeof BaseUserSchema>;

// User Creation Schema
export const CreateUserSchema = BaseUserSchema.extend({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
});

export type CreateUser = z.infer<typeof CreateUserSchema>;

// User Update Schema
export const UpdateUserSchema = BaseUserSchema.partial().extend({
  userId: z.string() // Required for updates
});

export type UpdateUser = z.infer<typeof UpdateUserSchema>;

// User Login Schema
export const UserLoginSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(1, 'Password is required'),
  deviceId: z.string().optional(),
  rememberMe: z.boolean().default(false)
});

export type UserLogin = z.infer<typeof UserLoginSchema>;

// User Response Schema (for API responses)
export const UserResponseSchema = BaseUserSchema.extend({
  userId: z.string()
});

export type UserResponse = z.infer<typeof UserResponseSchema>;

// User with Password Hash (for internal use)
export const UserWithPasswordSchema = BaseUserSchema.extend({
  userId: z.string(),
  passwordHash: z.string(),
  salt: z.string().optional()
});

export type UserWithPassword = z.infer<typeof UserWithPasswordSchema>;

// User Session Schema
export const UserSessionSchema = z.object({
  userId: z.string(),
  role: UserRoleSchema,
  email: z.string(),
  name: z.string(),
  preferences: UserPreferencesSchema,
  deviceId: z.string(),
  lastActivity: z.date()
});

export type UserSession = z.infer<typeof UserSessionSchema>; 